import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
    children?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    // Define a constant to check if we're on the forgot password page
    const isForgotPasswordPage = location.pathname === '/forgot-password';
    
    // useEffect must be called unconditionally
    useEffect(() => {
        const checkAuth = () => {
            // Skip auth check for forgot password page
            if (isForgotPasswordPage) {
                return;
            }
            
            const token = authService.getToken();
            if (!token) {
                navigate('/', { replace: true });
                return;
            }

            try {
                const userData = authService.getUser();
                if (!userData || !userData.id) {
                    authService.logout();
                    navigate('/', { replace: true });
                }
            } catch (error) {
                                authService.logout();
                navigate('/', { replace: true });
            }
        };

        if (!isLoading) {
            checkAuth();
        }
    }, [location.pathname, navigate, isLoading, isForgotPasswordPage]);

    // If we're on the forgot password page, allow access immediately
    if (isForgotPasswordPage) {
        return children ? <>{children}</> : <Outlet />;
    }
    
    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
                <div className="text-center">
                    <svg className="animate-spin h-8 w-8 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="mt-2 text-neutral-600 dark:text-neutral-400">Verifying authentication...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // Render children or outlet if authenticated
    return children ? <>{children}</> : <Outlet />;
};